import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Shield, Menu, X, LogOut, User, LayoutDashboard, NotebookPen, Heart, Inbox, Globe, ChevronDown, Search, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/fathers-rights", label: "Fathers' Rights" },
  { to: "/child-custody", label: "Child Custody" },
  { to: "/family-court", label: "Family Court" },
  { to: "/states", label: "States" },
  { to: "/resources", label: "Resources" },
  { to: "/community", label: "Community" },
  { to: "/blog", label: "Our Story" },
];
const TOOLS = [
  { to: "/legal-tips", label: "Legal Tips" }, { to: "/documents", label: "Legal Documents" }, { to: "/attorneys", label: "Find Attorneys" },
  { to: "/captains", label: "State Captains" }, { to: "/state-laws", label: "Compare State Laws" }, { to: "/search", label: "Search the site" },
  { to: "/reach-out", label: "Reach Out" },
];
const MOBILE_TOOLS = TOOLS.filter((t) => t.to !== "/reach-out");

export const Navbar = ({ onOpenAuth }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors px-1 py-1 ${
      isActive ? "text-amber-400" : "text-slate-300 hover:text-white"
    }`;

  return (
    <header className="fixed top-0 inset-x-0 z-50 aff-glass border-b border-[#223552]/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo-link">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#E5A93C] to-[#C86A36] flex items-center justify-center shadow-lg">
            <Shield className="h-5 w-5 text-[#0B111E]" strokeWidth={2.5} />
          </div>
          <div className="leading-none">
            <span className="font-heading font-extrabold text-white text-lg tracking-tight">A Father's Fight</span>
            <span className="hidden sm:block text-[10px] uppercase tracking-[0.25em] text-amber-400/80">Custody · Rights · Hope</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" className="hidden lg:inline-flex text-slate-300 hover:text-white hover:bg-[#172338] gap-1 px-3" data-testid="nav-tools">Tools <ChevronDown className="h-4 w-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#111B2C] border-[#223552] text-slate-200 w-52">
              {TOOLS.map((t) => <DropdownMenuItem key={t.to} asChild className="cursor-pointer focus:bg-[#223552]"><Link to={t.to} data-testid={`nav-tool-${t.to.slice(1).replace(/\//g, "-")}`}>{t.label}</Link></DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button asChild variant="ghost" className="hidden md:inline-flex text-slate-300 hover:text-white hover:bg-[#172338] px-2" data-testid="nav-search"><Link to="/search" aria-label="Search"><Search className="h-4 w-4" /></Link></Button>
          <Button asChild variant="ghost" className="hidden md:inline-flex text-amber-400 hover:text-amber-300 hover:bg-[#172338] gap-1.5 px-3" data-testid="nav-donate">
            <Link to="/captains#donate"><Heart className="h-4 w-4" /> Donate</Link>
          </Button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="text-slate-200 hover:text-white gap-2" data-testid="nav-user-menu">
                  <div className="h-7 w-7 rounded-full bg-[#223552] flex items-center justify-center">
                    <User className="h-4 w-4 text-amber-400" />
                  </div>
                  <span className="hidden sm:inline max-w-[120px] truncate">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#172338] border-[#223552] text-slate-200">
                <div className="px-2 py-1.5 text-xs text-slate-400">{user.email}</div>
                {user.role === "admin" && <div className="px-2 pb-1.5 text-xs text-amber-400">Moderator</div>}
                <DropdownMenuSeparator className="bg-[#223552]" />
                {user.role === "admin" && (
                  <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#223552]">
                    <Link to="/admin" data-testid="nav-admin"><Inbox className="h-4 w-4 mr-2" /> Admin Inbox</Link>
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#223552]">
                    <Link to="/admin/seo" data-testid="nav-admin-seo"><Search className="h-4 w-4 mr-2" /> SEO Manager</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#223552]">
                  <Link to="/dashboard" data-testid="nav-dashboard"><LayoutDashboard className="h-4 w-4 mr-2" /> My Dashboard</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#223552]">
                  <Link to="/journal" data-testid="nav-journal"><NotebookPen className="h-4 w-4 mr-2" /> My Case Journal</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#223552]">
                  <Link to="/research" data-testid="nav-research"><Globe className="h-4 w-4 mr-2" /> Research History</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer focus:bg-[#223552]">
                  <Link to="/community" data-testid="nav-my-community">Community Forum</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#223552]" />
                <DropdownMenuItem onClick={logout} className="cursor-pointer focus:bg-[#223552] text-crimson" data-testid="nav-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={onOpenAuth}
              className="hidden sm:inline-flex bg-[#E5A93C] hover:bg-[#F5BA4E] text-[#0B111E] font-semibold rounded-full px-5"
              data-testid="nav-join-button"
            >
              Join / Sign In
            </Button>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden text-slate-200" data-testid="nav-mobile-toggle">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#0B111E] border-[#223552] w-[300px] max-w-[88vw] p-0 flex flex-col overflow-y-auto [&>button]:hidden">
              <div className="p-5 border-b border-[#223552] flex items-center justify-between sticky top-0 bg-[#0B111E] z-10">
                <span className="font-heading font-bold text-white">Menu</span>
                <button onClick={() => setOpen(false)} className="h-10 w-10 -mr-2 rounded-lg flex items-center justify-center text-slate-400 hover:text-white" aria-label="Close menu" data-testid="nav-mobile-close"><X className="h-5 w-5" /></button>
              </div>
              <nav className="flex flex-col p-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
                {!user && (
                  <Button
                    onClick={() => { setOpen(false); onOpenAuth(); }}
                    className="mb-2 mx-2 h-11 bg-[#E5A93C] hover:bg-[#F5BA4E] text-[#0B111E] font-semibold rounded-full"
                    data-testid="nav-mobile-join"
                  >
                    Join / Sign In
                  </Button>
                )}
                {user && <p className="px-4 pt-2 pb-2 text-sm text-slate-400">Signed in as <span className="text-white">{user.name}</span></p>}
                {LINKS.map((l) => (
                  <NavLink
                    key={l.to} to={l.to} onClick={() => setOpen(false)}
                    className={({ isActive }) => `px-4 py-3 rounded-lg text-base font-medium ${isActive ? "bg-[#172338] text-amber-400" : "text-slate-300"}`}
                    data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {l.label}
                  </NavLink>
                ))}
                {user && (
                  <>
                    <NavLink to="/dashboard" onClick={() => setOpen(false)} className={({ isActive }) => `px-4 py-3 rounded-lg text-base font-medium ${isActive ? "bg-[#172338] text-amber-400" : "text-slate-300"}`} data-testid="nav-mobile-dashboard">My Dashboard</NavLink>
                    <NavLink to="/journal" onClick={() => setOpen(false)} className={({ isActive }) => `px-4 py-3 rounded-lg text-base font-medium ${isActive ? "bg-[#172338] text-amber-400" : "text-slate-300"}`} data-testid="nav-mobile-journal">My Case Journal</NavLink>
                    <NavLink to="/research" onClick={() => setOpen(false)} className={({ isActive }) => `px-4 py-3 rounded-lg text-base font-medium ${isActive ? "bg-[#172338] text-amber-400" : "text-slate-300"}`} data-testid="nav-mobile-research">Research History</NavLink>
                  </>
                )}
                <p className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-[0.25em] text-slate-500">Tools</p>
                {MOBILE_TOOLS.map((t) => <NavLink key={t.to} to={t.to} onClick={() => setOpen(false)} className="px-4 py-3 rounded-lg text-base font-medium text-slate-300" data-testid={`nav-mobile-tool-${t.to.slice(1)}`}>{t.label}</NavLink>)}
                <Link to="/captains#donate" onClick={() => setOpen(false)} className="px-4 py-3 rounded-lg text-base font-medium text-amber-400 flex items-center gap-2" data-testid="nav-mobile-donate"><Heart className="h-4 w-4" /> Donate</Link>
                <div className="mt-3 mx-2 pt-4 border-t border-[#223552]">
                  <Link to="/reach-out" onClick={() => setOpen(false)} className="flex items-center justify-center gap-2 h-12 rounded-full border border-amber-400/50 bg-amber-400/10 text-amber-300 font-semibold hover:bg-amber-400/20" data-testid="nav-mobile-reach-out"><HeartHandshake className="h-5 w-5" /> Reach Out</Link>
                  <p className="text-center text-[11px] text-slate-500 mt-2">Questions, concerns, or just need to talk.</p>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
